import { atom } from 'jotai';
import type { GeoJsonProperties } from 'geojson';
import { daySummaryModeAtom, selectedDateAtom, timeNavigatedAtom } from './atoms';
import type { MapView } from '../services/types';
import { fetchLatestEventSnapshot } from '../services/weatherHistory';

// The lookup a click has in flight. A later click aborts it, which cancels the
// server's query as well.
const inFlightLookupAtom = atom<AbortController | null>(null);

/**
 * Navigates the time selection to the newest history snapshot that holds the
 * clicked event, so the historical layers draw the event just clicked rather
 * than whatever time was selected before, at which the event may not exist.
 */
export const selectEventSnapshotAtom = atom(
  null,
  async (get, set, mapView: MapView, properties: NonNullable<GeoJsonProperties>) => {
    get(inFlightLookupAtom)?.abort();
    const controller = new AbortController();
    set(inFlightLookupAtom, controller);

    let snapshot: Date | null;
    try {
      snapshot = await fetchLatestEventSnapshot(mapView, properties, controller.signal);
    } catch (error) {
      if (controller.signal.aborted) {
        return;
      }
      console.error('Error looking up the clicked event snapshot:', error);
      set(inFlightLookupAtom, null);
      return;
    }
    // Abort is best-effort: a superseded lookup can still resolve, and its
    // answer must not move the selection away from the click that replaced it.
    if (get(inFlightLookupAtom) !== controller) {
      return;
    }
    set(inFlightLookupAtom, null);

    if (snapshot == null) {
      // The list read the event from the current table, which a workflow run
      // commits before its history snapshot, so a click in that window finds
      // no archived row. The live view is then the one view holding the event;
      // the day summary would overlay a history window that lacks it.
      console.warn(`No ${mapView} history snapshot holds the clicked event yet:`, properties);
      set(selectedDateAtom, new Date());
      set(timeNavigatedAtom, false);
      set(daySummaryModeAtom, false);
      return;
    }
    set(selectedDateAtom, snapshot);
  }
);
